import * as vp from 'vopay';

import { FunctionComponent } from 'react';
import { useLocation } from 'react-router-dom';

import { Balances as Balance } from './types';

const { formatAsCurrency, CurrencySymbols, Box, SubTitle, Table } = vp;

type KeyValuePair = {
  display: string;
  value: string;
};

type TablePropsHandlers = {
  balance: () => vp.TableProps;
};

type FC = FunctionComponent<Record<string, never>>;

const Balances: FC = () => {
  const query = new URLSearchParams(useLocation().search);
  const balance: Balance = {
    account: query.get('account') || '0',
    pending: query.get('pending') || '0',
    available: query.get('available') || '0',
    immediate: query.get('immediate') || '0',
    reserve: query.get('reserve') || '0',
    currency: (query.get('currency') as vp.SupportedCurrencies) || 'CAD',
    isActive: query.get('isActive') === 'true',
    accountStatus: query.get('accountStatus') || '',
    security: query.get('security') || '',
  };

  return (
    <div>
      <Box mt="lg" mb="md">
        <SubTitle subTitle="Balances" />
      </Box>
      <Table {...getTableProps('balance')} />
    </div>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      balance: () => {
        const balanceInfoPairs: KeyValuePair[] = [
          {
            display: 'Account Balance',
            value: `${formatAsCurrency(balance.account, CurrencySymbols[balance.currency])} ${balance.currency}`,
          },
          {
            display: 'Pending Balance',
            value: `${formatAsCurrency(balance.pending, CurrencySymbols[balance.currency])} ${balance.currency}`,
          },
          {
            display: 'Available Balance',
            value: `${formatAsCurrency(balance.available, CurrencySymbols[balance.currency])} ${balance.currency}`,
          },
          {
            display: 'Available Immediately',
            value: `${formatAsCurrency(balance.immediate, CurrencySymbols[balance.currency])} ${balance.currency}`,
          },
          {
            display: 'Reserve Amount',
            value: `${formatAsCurrency(balance.reserve, CurrencySymbols[balance.currency])} ${balance.currency}`,
          },
        ];

        const balanceInfoProps: vp.TableProps = {
          name: 'account-balances',
          variant: 'pair',
          columns: {
            detailKey: { header: 'Balances' },
            detailValue: { header: '' },
          },
          rows: balanceInfoPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return balanceInfoProps;
      },
    };

    return handlers[handler]();
  }
};

export default Balances;
